import React, { useState, useEffect, useMemo, useRef } from "react";
import "./PicklistMappingModal.scss";
import PicklistValueDropdown from "./PicklistValueDropdown";

const PicklistMappingModal = ({
  visible,
  onClose,
  onSave,
  targetFieldName,
  targetFieldLabel,
  sourceFieldApiName,
  acePicklistValues = [],
  hubspotPicklistValues = [],
  existingMappings = [],
  isDealStage = false,
  stagePipelines = [],
  pipelinePicklistMappings = {},
}) => {
  const [picklistMappings, setPicklistMappings] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState("");
  const [isPipelineDropdownOpen, setIsPipelineDropdownOpen] = useState(false);
  const pipelineDropdownRef = useRef(null);

  // Temporary storage for all pipeline mappings (only committed on Save)
  const [tempPipelineMappings, setTempPipelineMappings] = useState({});

  // Get stages from selected pipeline (normalized to { value, label })
  const availableHubspotValues = useMemo(() => {
    if (isDealStage && selectedPipeline && stagePipelines.length > 0) {
      const pipeline = stagePipelines.find((p) => p.id === selectedPipeline);
      if (pipeline && pipeline.stages) {
        // Return stages sorted by displayOrder and normalized to { value, label }
        return pipeline.stages
          .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
          .map((stage) => ({
            value: stage.id,
            label: stage.label || stage.id,
          }));
      }
    }

    // Fallback to original hubspotPicklistValues (normalize to { value, label })
    if (Array.isArray(hubspotPicklistValues)) {
      if (hubspotPicklistValues.length > 0 && typeof hubspotPicklistValues[0] === "object") {
        return hubspotPicklistValues.map((opt) => ({
          value: opt.value,
          label: opt.label || opt.value,
        }));
      }
      
      return hubspotPicklistValues.map((val) => ({
        value: val,
        label: val,
      }));
    }

    return [];
  }, [isDealStage, selectedPipeline, stagePipelines, hubspotPicklistValues]);

  // Initialize temp mappings from pipelinePicklistMappings when modal opens
  useEffect(() => {
    if (visible && isDealStage && stagePipelines.length > 0) {
      // Initialize temp storage with existing pipeline mappings
      setTempPipelineMappings(JSON.parse(JSON.stringify(pipelinePicklistMappings)));
    } else if (visible && acePicklistValues.length > 0) {
      // For non-pipeline fields, initialize normally
      const mappings = acePicklistValues.map((aceValue) => {
        const existingMapping = existingMappings.find(
          (m) => m.targetValue === aceValue
        );

        return {
          targetValue: aceValue,
          sourceValue: existingMapping ? existingMapping.sourceValue : "",
          isRequired: false,
        };
      });

      setPicklistMappings(mappings);
    }

    // Reset when modal closes
    if (!visible) {
      setSelectedPipeline("");
      setTempPipelineMappings({});
    }
  }, [visible, acePicklistValues, existingMappings, isDealStage, stagePipelines, pipelinePicklistMappings]);

  // Set default pipeline to first one when modal opens
  useEffect(() => {
    if (visible && isDealStage && stagePipelines.length > 0 && !selectedPipeline) {
      const sortedPipelines = [...stagePipelines].sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
      );
      if (sortedPipelines.length > 0) {
        setSelectedPipeline(sortedPipelines[0].id);
      }
    }
  }, [visible, isDealStage, stagePipelines]);

  const handleMappingChange = (aceValue, hubspotValue) => {
    setPicklistMappings((prev) =>
      prev.map((mapping) =>
        mapping.targetValue === aceValue
          ? { ...mapping, sourceValue: hubspotValue }
          : mapping
      )
    );

    // Save to temp storage for current pipeline
    if (isDealStage && selectedPipeline && stagePipelines.length > 0) {
      setTempPipelineMappings((prev) => {
        const currentPipelineMappings = prev[selectedPipeline]?.[targetFieldName] || [];
        const updatedMappings = currentPipelineMappings.map((m) =>
          m.targetValue === aceValue ? { ...m, sourceValue: hubspotValue } : m
        );

        // If mapping doesn't exist, add it
        if (!currentPipelineMappings.find((m) => m.targetValue === aceValue)) {
          updatedMappings.push({ targetValue: aceValue, sourceValue: hubspotValue });
        }

        return {
          ...prev,
          [selectedPipeline]: {
            ...prev[selectedPipeline],
            [targetFieldName]: updatedMappings,
          },
        };
      });
    }
  };

  // Get all selected values across all mappings (for disabling already used options)
  const getAllSelectedValues = (currentIndex) => {
    const selectedSet = new Set();

    // Collect values from current pipeline's mappings
    picklistMappings.forEach((mapping, index) => {
      if (index !== currentIndex && mapping.sourceValue && mapping.sourceValue.trim() !== "") {
        // Split comma-separated values and add to set (trim before split to handle edge cases)
        const values = mapping.sourceValue.split(",").map(v => v.trim());
        values.forEach(v => {
          if (v) selectedSet.add(v);
        });
      }
    });

    // If this is a multi-pipeline field (Deal Stage), also collect values from other pipelines
    // if (isDealStage && stagePipelines.length > 0) {
    //   Object.keys(tempPipelineMappings).forEach((pipelineId) => {
    //     // Skip current pipeline (already handled above)
    //     if (pipelineId === selectedPipeline) {
    //       return;
    //     }

    //     // Get mappings for this pipeline and target field
    //     const otherPipelineMappings = tempPipelineMappings[pipelineId]?.[targetFieldName] || [];
    //     otherPipelineMappings.forEach((mapping) => {
    //       if (mapping.sourceValue && mapping.sourceValue.trim() !== "") {
    //         // Trim sourceValue before split to avoid comparison issues with spaces
    //         const values = mapping.sourceValue.trim().split(",").map(v => v.trim());
    //         values.forEach(v => {
    //           if (v) selectedSet.add(v);
    //         });
    //       }
    //     });
    //   });
    // }

    return selectedSet;
  };

  const handleReset = () => {
    setPicklistMappings((prev) =>
      prev.map((mapping) => ({
        ...mapping,
        sourceValue: "",
      }))
    );
  };

  const handleAutoMap = () => {
    // Auto-map only exact matches (case insensitive), leave empty if no match
    setPicklistMappings((prev) =>
      prev.map((mapping) => {
        // Try to find exact match by label (case insensitive)
        const matchingValue = availableHubspotValues.find(
          (hsValue) =>
            String(hsValue.label).toLowerCase() === String(mapping.targetValue).toLowerCase()
        );

        return {
          ...mapping,
          sourceValue: matchingValue ? matchingValue.value : "",
        };
      })
    );
  };

  // Load mappings when pipeline changes (from temp storage)
  useEffect(() => {
    if (isDealStage && selectedPipeline && acePicklistValues.length > 0) {
      // Get mappings from temp storage first, fallback to original data
      const pipelineMappings = tempPipelineMappings[selectedPipeline]?.[targetFieldName] || [];

      // Initialize mappings with data from temp storage
      const mappings = acePicklistValues.map((aceValue) => {
        const existingMapping = pipelineMappings.find(
          (m) => m.targetValue === aceValue
        );

        return {
          targetValue: aceValue,
          sourceValue: existingMapping ? existingMapping.sourceValue : "",
          isRequired: false,
        };
      });

      setPicklistMappings(mappings);
    }
  }, [selectedPipeline, isDealStage, acePicklistValues, tempPipelineMappings, targetFieldName]);

  // Close pipeline dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pipelineDropdownRef.current && !pipelineDropdownRef.current.contains(event.target)) {
        setIsPipelineDropdownOpen(false);
      }
    };

    if (isPipelineDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPipelineDropdownOpen]);

  const handleSave = () => {
    // If stagePipelines exists and has items, save all pipelines mappings from temp storage
    if (stagePipelines && stagePipelines.length > 0) {
      // First, save current pipeline's mappings to temp storage
      const mappingsToSave = picklistMappings
        .filter((m) => m.sourceValue && m.sourceValue.trim() !== "")
        .map((m) => ({
          targetValue: m.targetValue,
          sourceValue: m.sourceValue,
        }));

      const updatedTempMappings = {
        ...tempPipelineMappings,
        [selectedPipeline]: {
          ...tempPipelineMappings[selectedPipeline],
          [targetFieldName]: mappingsToSave,
        },
      };

      // Send all pipelines data at once
      const saveData = {
        targetFieldName,
        hasPipelines: true,
        allPipelineMappings: updatedTempMappings,
      };
      onSave(saveData);
    } else {
      // No pipelines, save as regular picklist mapping
      const mappingsToSave = picklistMappings
        .filter((m) => m.sourceValue && m.sourceValue.trim() !== "")
        .map((m) => ({
          targetValue: m.targetValue,
          sourceValue: m.sourceValue,
        }));

      const saveData = {
        targetFieldName,
        mappings: mappingsToSave,
        pipelineId: null,
      };
      onSave(saveData);
    }

    onClose();
  };
  
  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="picklist-modal-backdrop"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="picklist-modal-container">
        <div className="picklist-modal">
          {/* Header */}
          <div className="picklist-modal-header">
            <h2>Map field values</h2>
            <p>
              Map ACE pipeline manager values to HubSpot values for{" "}
              <strong>{targetFieldLabel}</strong>.
            </p>
          </div>

          {/* Content */}
          <div className="picklist-modal-content">
            {isDealStage && stagePipelines.length > 0 && (
              <div
                className="pipeline-selector"
                style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "0px", flexWrap: "wrap" }}
              >
                <label style={{ fontWeight: 500, minWidth: "140px" }}>
                  Select Pipeline :
                </label>
                <div className="picklist-custom-dropdown" ref={pipelineDropdownRef} style={{ flex: "1 1 320px", maxWidth: "280px" }}>
                  <div
                    className={`picklist-dropdown-header ${isPipelineDropdownOpen ? 'open' : ''}`}
                    onClick={() => setIsPipelineDropdownOpen(!isPipelineDropdownOpen)}
                  >
                    <span className="picklist-dropdown-text">
                      {selectedPipeline
                        ? stagePipelines.find((p) => p.id === selectedPipeline)?.label || "-- Select pipeline --"
                        : "-- Select pipeline --"}
                    </span>
                    <svg
                      className={`picklist-dropdown-arrow ${isPipelineDropdownOpen ? "open" : ""}`}
                      width="10"
                      height="6"
                      viewBox="0 0 10 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1L5 5L9 1"
                        stroke="#706E6B"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  {isPipelineDropdownOpen && (
                    <div className="picklist-dropdown-menu">
                      <div className="picklist-dropdown-options">
                        {stagePipelines
                          .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                          .map((pipeline) => (
                            <div
                              key={pipeline.id}
                              className={`picklist-dropdown-option ${selectedPipeline === pipeline.id ? "selected" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPipeline(pipeline.id);
                                setIsPipelineDropdownOpen(false);
                              }}
                            >
                              <span className="picklist-option-label">{pipeline.label}</span>
                              {selectedPipeline === pipeline.id && (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path
                                    d="M13.3337 4L6.00033 11.3333L2.66699 8"
                                    stroke="#40A0C1"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="picklist-grid">
              {/* ACE Values Column */}
              <div className="picklist-column">
                <h3>ACE Pipeline Manager Values</h3>
                <div className="picklist-values">
                  {picklistMappings.map((mapping, index) => (
                    <div key={index} className="picklist-value-row">
                      {mapping.isRequired && (
                        <span className="picklist-value-required">*</span>
                      )}
                      <span className="picklist-value-label">
                        {mapping.targetValue}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* HubSpot Values Column */}
              <div className="picklist-column">
                <h3>HubSpot Values</h3>
                <div className="picklist-values">
                  {picklistMappings.map((mapping, index) => {
                    const disabledOptions = getAllSelectedValues(index);

                    return (
                      <div key={index} className="picklist-value-row">
                        <PicklistValueDropdown
                          value={mapping.sourceValue}
                          onChange={(value) =>
                            handleMappingChange(mapping.targetValue, value)
                          }
                          options={availableHubspotValues}
                          disabledOptions={disabledOptions}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="picklist-modal-footer">
            <button className="btn-secondary" onClick={onClose}>
              Close
            </button>
            <button className="btn-secondary" onClick={handleReset}>
              Reset (Need Save)
            </button>
            <button className="btn-secondary" onClick={handleAutoMap}>
              Auto-Mapping
            </button>
            <button className="btn-primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PicklistMappingModal;
